'use client'

import { useState, useCallback } from 'react'
import Image from 'next/image'
import { Reveal } from '@/components/Reveal'

interface Episode {
  id: string
  title: string
  guest?: string | null
  description?: string | null
  publishedDate: string
  duration?: string | null
  thumbnailUrl: string | null
  spotifyEpisodeId: string
}

interface PodcastPlayerClientProps {
  episodes: Episode[]
  showId: string
  coverImageUrl: string | null
  layout: 'grid' | 'list'
  episodesPerPage: number
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  })
}

export function PodcastPlayerClient({
  episodes,
  showId,
  coverImageUrl,
  layout,
  episodesPerPage,
}: PodcastPlayerClientProps) {
  const [activeEpisodeId, setActiveEpisodeId] = useState<string>(
    episodes[0]?.spotifyEpisodeId || '',
  )
  const [playerRevealed, setPlayerRevealed] = useState(false)
  const [visibleCount, setVisibleCount] = useState(episodesPerPage)

  const handlePlay = useCallback((episodeId: string) => {
    setActiveEpisodeId(episodeId)
    setPlayerRevealed(true)
    const player = document.getElementById('podcast-player')
    if (player && window.innerWidth < 1024) {
      player.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
  }, [])

  const handleReveal = useCallback(() => {
    setPlayerRevealed(true)
  }, [])

  const embedUrl = activeEpisodeId
    ? `https://open.spotify.com/embed/episode/${activeEpisodeId}/video?theme=0`
    : `https://open.spotify.com/embed/show/${showId}/video?theme=0`

  const activeEp = episodes.find((e) => e.spotifyEpisodeId === activeEpisodeId)
  const visibleEpisodes = episodes.slice(0, visibleCount)

  return (
    <div>
      {/* Player + Episode Sidebar */}
      <div id="podcast-player" className="flex flex-col lg:flex-row gap-8 mb-12">
        {/* Video Player — vertical format */}
        <div className="w-full lg:w-auto lg:shrink-0">
          <div
            className="relative w-full lg:w-[400px] xl:w-[440px]"
            style={{ aspectRatio: '9/16' }}
          >
            {playerRevealed ? (
              <iframe
                key={embedUrl}
                src={embedUrl}
                width="100%"
                height="100%"
                allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture"
                loading="lazy"
                className="absolute inset-0 w-full h-full"
                title="Spotify Podcast Player"
              />
            ) : (
              <button
                onClick={handleReveal}
                className="absolute inset-0 w-full h-full cursor-pointer group overflow-hidden bg-[#041f43]"
              >
                {coverImageUrl && (
                  <Image
                    src={coverImageUrl}
                    alt="Podcast cover"
                    fill
                    className="object-contain transition-transform duration-500 group-hover:scale-105"
                    sizes="(max-width: 1024px) 100vw, 440px"
                    priority
                  />
                )}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors pointer-events-none" />
                <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
                  <div className="w-20 h-20 rounded-full bg-background/90 group-hover:bg-background flex items-center justify-center transition-colors shadow-lg">
                    <svg
                      className="w-8 h-8 text-foreground ml-1"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M8 5v14l11-7z" />
                    </svg>
                  </div>
                  <span className="mt-4 text-primary-foreground text-sm font-medium tracking-wide">
                    Watch on Spotify
                  </span>
                </div>
              </button>
            )}
          </div>
        </div>

        {/* Episode Sidebar */}
        <div className="flex-1 min-w-0">
          {/* Active episode info */}
          {activeEp && (
            <div className="mb-6">
              <h3 className="text-xl font-medium text-foreground">{activeEp.title}</h3>
              {activeEp.guest && (
                <p className="text-sm text-foreground/60 mt-1">with {activeEp.guest}</p>
              )}
              {activeEp.description && (
                <p className="text-sm text-foreground/50 mt-3 leading-relaxed">
                  {activeEp.description}
                </p>
              )}
            </div>
          )}

          {/* Episode list in sidebar */}
          {episodes.length > 1 && (
            <div>
              <h4 className="text-sm font-medium text-foreground/40 uppercase tracking-wider mb-4">
                Episodes
              </h4>
              <div className="space-y-1 max-h-[500px] overflow-y-auto pr-2">
                {episodes.map((ep, index) => {
                  const isActive = ep.spotifyEpisodeId === activeEpisodeId
                  return (
                    // Steve 2026-08-24 — per-row slideup reveal, capped at 5 rows so a
                    // long episode list doesn't leave later rows waiting out an
                    // ever-growing delay after they've already scrolled into view.
                    <Reveal key={ep.id} variant="slideup" delay={Math.min(index, 5) * 100}>
                      <button
                        onClick={() => handlePlay(ep.spotifyEpisodeId)}
                        className={`w-full flex items-center gap-3 p-3 text-left transition-colors ${
                          isActive ? 'bg-foreground/5' : 'hover:bg-foreground/[0.02]'
                        }`}
                      >
                        <div className="relative w-12 h-12 shrink-0 bg-muted overflow-hidden">
                          {ep.thumbnailUrl ? (
                            <Image
                              src={ep.thumbnailUrl}
                              alt={ep.title}
                              fill
                              className="object-cover"
                              sizes="48px"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-foreground/20">
                              <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p
                            className={`text-sm font-medium truncate ${
                              isActive ? 'text-foreground' : 'text-foreground/80'
                            }`}
                          >
                            {ep.title}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5 text-xs text-foreground/40">
                            {ep.guest && <span className="truncate">{ep.guest}</span>}
                            {ep.guest && ep.duration && <span>·</span>}
                            {ep.duration && <span>{ep.duration}</span>}
                          </div>
                        </div>
                        {isActive && (
                          <div className="shrink-0 flex items-center gap-0.5">
                            <span className="w-0.5 h-2.5 bg-foreground animate-pulse" />
                            <span className="w-0.5 h-3.5 bg-foreground animate-pulse [animation-delay:150ms]" />
                            <span className="w-0.5 h-2 bg-foreground animate-pulse [animation-delay:300ms]" />
                          </div>
                        )}
                      </button>
                    </Reveal>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
